import React, { useEffect, useState } from 'react';
import { meetingService } from '../../services/meetingService';
import {
  EMPTY_FIRST_STEP_SIGNUP,
  FirstStepSignupConfig,
} from '../../types/workshopFirstStepSignup';
import styles from '../../styles/meetingSettingsPage.module.css';

interface WorkshopFirstStepSignupSettingsProps {
  meetingCode: string;
  canEdit: boolean;
  onReload: () => Promise<void>;
}

const WorkshopFirstStepSignupSettings: React.FC<WorkshopFirstStepSignupSettingsProps> = ({
  meetingCode,
  canEdit,
  onReload,
}) => {
  const [config, setConfig] = useState<FirstStepSignupConfig>(EMPTY_FIRST_STEP_SIGNUP);
  const [weekCountInput, setWeekCountInput] = useState('10');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    meetingService
      .getFirstStepSignup(meetingCode)
      .then((data) => {
        if (!cancelled) {
          setConfig(data);
          setWeekCountInput(String(data.week_count));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfig(EMPTY_FIRST_STEP_SIGNUP);
          setWeekCountInput('10');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingCode]);

  const flash = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3500);
  };

  const save = async (next: FirstStepSignupConfig) => {
    if (!canEdit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload: FirstStepSignupConfig = {
        ...next,
        assignments: config.assignments,
        locked_week_indexes: config.locked_week_indexes,
      };
      await meetingService.updateMeeting(meetingCode, { first_step_signup: payload });
      setConfig(payload);
      setWeekCountInput(String(payload.week_count));
      await onReload();
      flash('First Step sign-up settings saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const applyWeekCount = () => {
    const n = Math.min(20, Math.max(5, Number(weekCountInput) || config.week_count));
    setWeekCountInput(String(n));
    void save({ ...config, week_count: n });
  };

  if (loading) {
    return <p className={styles.fieldHint}>Loading First Step sign-up settings…</p>;
  }

  return (
    <section className={styles.settingsSection}>
      <h2 className={styles.sectionTitle}>First Step presentation sign-up</h2>
      <p className={styles.fieldHint}>
        Enable when the workshop begins Step One. Members sign up on the Workshop tab. Disable when
        Step One is complete — the schedule stays visible as a read-only archive.
      </p>
      {message && <p className={styles.saveMessage}>{message}</p>}
      {error && <p className={styles.errorInline}>{error}</p>}

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={config.enabled}
          onChange={(e) => void save({ ...config, enabled: e.target.checked })}
          disabled={!canEdit || saving}
        />
        Sign-up enabled
      </label>

      <div className={styles.formGrid} style={{ marginTop: '1rem' }}>
        <div>
          <label className={styles.label}>Standard presenters (1–6)</label>
          <select
            className={styles.input}
            value={config.standard_slot_count}
            onChange={(e) =>
              void save({ ...config, standard_slot_count: Number(e.target.value) })
            }
            disabled={!canEdit || saving}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={styles.label}>Alternate presenters (1–3)</label>
          <select
            className={styles.input}
            value={config.alternate_slot_count}
            onChange={(e) =>
              void save({ ...config, alternate_slot_count: Number(e.target.value) })
            }
            disabled={!canEdit || saving}
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={styles.label}>First presentation week</label>
          <input
            type="date"
            className={styles.input}
            value={config.first_presentation_week_date || ''}
            onChange={(e) =>
              setConfig((c) => ({ ...c, first_presentation_week_date: e.target.value }))
            }
            onBlur={() => {
              setConfig((c) => {
                void save(c);
                return c;
              });
            }}
            disabled={!canEdit || saving}
          />
          <p className={styles.fieldHint}>Each row adds 7 days from this date.</p>
        </div>
        <div>
          <label className={styles.label}>Week count (5–20)</label>
          <div className={styles.inlineActions}>
            <input
              type="number"
              min={5}
              max={20}
              className={styles.input}
              style={{ width: '5rem' }}
              value={weekCountInput}
              onChange={(e) => setWeekCountInput(e.target.value)}
              disabled={!canEdit || saving}
            />
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={applyWeekCount}
              disabled={!canEdit || saving}
            >
              Apply
            </button>
          </div>
          <p className={styles.fieldHint}>
            Cannot reduce week count while someone is signed up in a removed week.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WorkshopFirstStepSignupSettings;
